import StoreTags from '@enums/StoreTags'
import { ItemsFromResponse } from '@models/common'
import {
  EndBody,
  EndResponse,
  ListenerCourseChapterThemeStatsResult,
  IspringTestResults,
  PauseBody,
  StartBody,
  StartResponse,
  TestResultsBody,
} from '@models/statistic'
import { EmployeeEducationStat } from '@models/employeeEducationStats'
import { createDownloadCsvReportQuery, createDownloadExcelReportQuery } from '@utils/files'
import emptySplitApi from '../api'
import { HYDRA_MEMBER, HYDRA_TOTAL_ITEMS } from '../../config/consts'

export type ListenerCourseChapterThemeStatsResultsQueryParams = {
  page?: number
  limit?: number
  course?: string
  listener?: string
  testSuccess?: 'true' | 'false'
  'endAt[after]'?: string
  'endAt[before]'?: string
}

const INVALIDATED_TAGS = [
  StoreTags.STATISTICS,
  StoreTags.COURSES,
  // COURSE (singular) держит getCourseById с finalTest.currentListenerStatus —
  // без него индикатор итогового не обновлялся после SCORM start/end до перезагрузки.
  StoreTags.COURSE,
  StoreTags.EMPLOYEE_EDUCATION_DESTINATIONS,
  StoreTags.EMPLOYEE_EDUCATION_DESTINATION,
  StoreTags.CERTIFICATES,
  StoreTags.COURSE_THEMES,
  StoreTags.COURSE_THEME,
  StoreTags.EMPLOYEE_EDUCATION_STATS,
]

export const statisticApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    postEnd: builder.mutation<EndResponse, EndBody>({
      invalidatesTags: INVALIDATED_TAGS,
      query: (body) => ({
        url: '/api/listener_course_chapter_theme_stats/end',
        method: 'POST',
        body,
      }),
    }),
    postStart: builder.mutation<StartResponse, StartBody>({
      invalidatesTags: INVALIDATED_TAGS,
      query: (body) => ({
        url: '/api/listener_course_chapter_theme_stats/start',
        method: 'POST',
        body,
      }),
    }),
    postPause: builder.mutation<void, PauseBody>({
      invalidatesTags: INVALIDATED_TAGS,
      query: (body) => ({
        url: '/api/listener_course_chapter_theme_stats/pause',
        method: 'POST',
        body,
      }),
    }),
    postTestResults: builder.mutation<void, TestResultsBody>({
      invalidatesTags: INVALIDATED_TAGS,
      query: (body) => ({
        url: '/api/listener_course_test_stats',
        method: 'POST',
        body,
      }),
    }),

    getEmployeeCourseStats: builder.query<
      { items: EmployeeEducationStat[]; length: number },
      number
    >({
      query: (id) => ({
        url: '/api/courses/employeeCoursesStats/' + id,
      }),
      transformResponse: (
        response: ItemsFromResponse<EmployeeEducationStat>
      ) => ({
        items: response[HYDRA_MEMBER].map((item) => ({
          ...item,
          title: item.title,
          code: item['@id'],
        })),
        length: response[HYDRA_TOTAL_ITEMS],
      }),
      providesTags: [StoreTags.EMPLOYEE_EDUCATION_STATS],
    }),
    postIspringTestResults: builder.mutation<void, IspringTestResults>({
      invalidatesTags: INVALIDATED_TAGS,
      query: (body) => ({
        url: '/api/listener_course_chapter_theme_stats/ispring',
        method: 'POST',
        body,
      }),
    }),

    getListenerCourseChapterThemeStatsResults: builder.query<
      { items: ListenerCourseChapterThemeStatsResult[]; length: number },
      ListenerCourseChapterThemeStatsResultsQueryParams
    >({
      query: (params) => ({
        url: '/api/listener_course_chapter_theme_stats/results',
        params,
      }),
      providesTags: [StoreTags.STATISTICS],
    }),

    getListenerCourseChapterThemeStatsResultsXlsx: builder.query<
      void,
      ListenerCourseChapterThemeStatsResultsQueryParams
    >({
      query: (params) =>
        createDownloadExcelReportQuery(
          '/api/listener_course_chapter_theme_stats/results/export/xlsx',
          'listener_course_chapter_theme_stats_results.xlsx'
        )(params),
    }),

    getListenerCourseChapterThemeStatsResultsCsv: builder.query<
      void,
      ListenerCourseChapterThemeStatsResultsQueryParams
    >({
      query: (params) =>
        createDownloadCsvReportQuery(
          '/api/listener_course_chapter_theme_stats/results/export/csv',
          'listener_course_chapter_theme_stats_results.csv'
        )(params),
    }),
  }),
})

export const {
  usePostEndMutation,
  usePostStartMutation,
  usePostPauseMutation,
  usePostTestResultsMutation,
  useGetEmployeeCourseStatsQuery,
  usePostIspringTestResultsMutation,
  useGetListenerCourseChapterThemeStatsResultsQuery,
  useLazyGetListenerCourseChapterThemeStatsResultsXlsxQuery,
  useLazyGetListenerCourseChapterThemeStatsResultsCsvQuery,
} = statisticApi
